// LIBRARY
import React from "react";
import PropTypes from "prop-types";
import { Button as AntButton, Modal, Typography, Card, Space } from "antd";
import { DownloadOutlined, InfoCircleOutlined, FileTextOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

/**
 * DownloadCSV - A shared component for exporting data to CSV.
 * Standardizes the look and feel of export buttons across the application.
 */
class DownloadCSV extends React.Component {
    state = {
        showModal: false,
        isLoading: false
    };

    toggleModal = () => {
        this.setState(prevState => ({ showModal: !prevState.showModal }));
    };

    handleConfirmedExport = async () => {
        const { onConfirm } = this.props;
        
        if (onConfirm && typeof onConfirm === 'function') {
            this.setState({ isLoading: true });
            try {
                const asyncData = await onConfirm();
                if (asyncData) {
                    this.executeDownload(asyncData);
                }
            } catch (error) {
                console.error("Export failed:", error);
            } finally {
                this.setState({ isLoading: false, showModal: false });
            }
        } else {
            this.toggleModal();
            this.executeDownload();
        }
    };

    executeDownload = (providedData) => {
        const { data, columns, filename, separator, addPadding } = this.props;
        const finalData = providedData || data;

        if (!finalData || finalData.length === 0) return;

        // Extract and escape headers
        const headers = columns.map(col => {
            let title = col.title || '';
            // Prepend a tab to force string treatment and left alignment in Excel
            const formattedTitle = addPadding ? `\t${title} ` : title;
            return `"${formattedTitle.replace(/"/g, '""')}"`;
        });
        
        // Extract and format rows
        const rows = finalData.map(record => {
            return columns.map(col => {
                let val = record[col.dataIndex];
                
                // Handle custom render or mapping
                if (col.render && typeof col.render === 'function') {
                    const rendered = col.render(val, record);
                    if (['string', 'number', 'boolean'].includes(typeof rendered)) {
                        val = rendered;
                    }
                }

                // Handle null/undefined and escape double quotes
                let stringVal = (val === null || val === undefined) ? '' : val.toString();
                
                // Prepend a tab \t to tell Excel "This is definitely a string"
                // This keeps it on the LEFT side and prevents removing leading zeros
                const formattedVal = addPadding ? `\t${stringVal} ` : stringVal;
                
                return `"${formattedVal.replace(/"/g, '""')}"`;
            });
        });

        // Combine headers and rows
        const csvContent = [
            headers.join(separator),
            ...rows.map(row => row.join(separator))
        ].join('\n');

        // Add UTF-8 BOM for Excel compatibility
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
        const formattedTimestamp = `${year}-${month}-${day}_${time}`;
        
        link.href = url;
        link.setAttribute('download', `${filename.replace('.csv', '')}_${formattedTimestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    render() {
        const { disabled, buttonText, confirmRequired, confirmDetails, isLookupTable } = this.props;
        const { showModal } = this.state;

        const modalTitle = confirmDetails.title 
            ? `Export ${confirmDetails.title}?` 
            : (isLookupTable && confirmDetails.section ? `Export ${confirmDetails.section}?` : 'Export Exam Result?');

        return (
            <>
                <AntButton
                    shape="round"
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={confirmRequired ? this.toggleModal : this.executeDownload}
                    disabled={disabled}
                >
                    {buttonText || 'EXPORT CSV'}
                </AntButton>

                <Modal
                    visible={showModal}
                    onCancel={this.toggleModal}
                    footer={[
                        <AntButton key="cancel" onClick={this.toggleModal} style={{ borderRadius: '4px' }}>
                            Cancel
                        </AntButton>,
                        <AntButton 
                            key="download" 
                            type="primary" 
                            onClick={this.handleConfirmedExport}
                            loading={this.state.isLoading}
                            style={{ backgroundColor: '#ff7875', border: 'none', borderRadius: '4px' }}
                        >
                            Download anyway
                        </AntButton>
                    ]}
                    centered
                    width={450}
                    closable={true}
                >
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <div style={{ flexShrink: 0 }}>
                            <InfoCircleOutlined style={{ fontSize: '24px', color: '#faad14' }} />
                        </div>
                        <div>
                            <Title level={5} style={{ marginTop: 0 }}>{modalTitle}</Title>
                            <Text type="secondary" style={{ display: 'block', marginBottom: '20px' }}>
                                You are about to export the following details:
                            </Text>

                            <Card 
                                size="small" 
                                style={{ 
                                    backgroundColor: 'transparent', 
                                    border: 'none', 
                                    marginBottom: '20px',
                                   
                                }}
                            >
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {isLookupTable ? (
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <Text strong>• All:</Text>
                                            <Text>{confirmDetails.section || 'N/A'}</Text>
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <Text strong>• Section:</Text>
                                                <Text>{confirmDetails.section || 'All'}</Text>
                                            </div>
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <Text strong>• Specimen:</Text>
                                                <Text>{confirmDetails.specimen || 'All'}</Text>
                                            </div>
                                        </>
                                    )}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Text strong>• Records:</Text>
                                        <Text>{confirmDetails.count || 0} {confirmDetails.count === 1 ? 'item' : 'items'}</Text>
                                    </div>
                                    {confirmDetails.search && (
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <Text strong>• Search:</Text>
                                            <Text>{confirmDetails.search}</Text>
                                        </div>
                                    )}
                                </Space>
                            </Card>

                            {(!isLookupTable || confirmDetails.search) && (
                                <Text style={{ color: '#ff4d4f', fontSize: '13px', fontWeight: 500 }}>
                                    Warning: Ensure the selected filters are correct before proceeding with the download.
                                </Text>
                            )}
                            <Text style={{ display: 'block', marginTop: isLookupTable ? '0px' : '15px', color: '#595959' }}>
                                Do you want to proceed with the export?
                            </Text>
                        </div>
                    </div>
                </Modal>
            </>
        );
    }
}

DownloadCSV.propTypes = {
    /** Array of data objects to export */
    data: PropTypes.array.isRequired,
    /** Array of column definitions { title, dataIndex, render } */
    columns: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        dataIndex: PropTypes.string.isRequired,
        render: PropTypes.func
    })).isRequired,
    /** Desired filename for the exported CSV */
    filename: PropTypes.string.isRequired,
    /** Disable state of the button */
    disabled: PropTypes.bool,
    /** Custom text for the button */
    buttonText: PropTypes.string,
    /** CSV separator string (default is comma) */
    separator: PropTypes.string,
    /** Whether to add padding/tab to force left alignment and visual spacing */
    addPadding: PropTypes.bool,
    /** Whether to show confirmation modal */
    confirmRequired: PropTypes.bool,
    /** Whether it's for a Lookup Table (simplifies modal) */
    isLookupTable: PropTypes.bool,
    /** Details to show in confirmation modal */
    confirmDetails: PropTypes.shape({
        title: PropTypes.string,
        section: PropTypes.string,
        specimen: PropTypes.string,
        count: PropTypes.number,
        search: PropTypes.string
    }),
    /** Optional async callback to fetch/process data before download */
    onConfirm: PropTypes.func
};

DownloadCSV.defaultProps = {
    disabled: false,
    buttonText: 'EXPORT CSV',
    separator: ',',
    addPadding: true,
    confirmRequired: false,
    isLookupTable: false,
    confirmDetails: {}
};

export default DownloadCSV;
